import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Typography, Icon, TextField, InputAdornment, List, ListItem, Collapse, ListItemText, ListItemIcon, Box } from '@mui/material';
import useStyles from './RealmsCheckpointsList.styles';
import cpUseStyles from '../../ChecklistScores/ChecklistScores.styles';
import { get, size, first } from 'global/lib/lodash';
import { Loader, useScreen } from 'global/styles';
import sendData from 'redux/actions/sendData';
import getHighlightedText from 'global/components/highlight-text';
import useDebounce from 'global/hooks/useDebounce';
import { filterData } from '../utils';
import ScoreIcon from 'global/components/ScoreIcon';
import { Row } from 'global/components/row';
import Switch from 'global/components/switch';
import { Text } from 'global/components/text';
import { createSubScore, deleteSubsOnCheckpoint } from './SubScoreLauncher';
import { SubScoreCollapse } from './SubScoreCollapse';
import { stringObj } from 'global/utils';
import { IconButton as IcoButton } from 'global/components/Button';
import { Button } from 'global/components/Button';
import { Column } from 'global/components/row';
import { AUTOTAG } from '../index';
import { PhotoFilter } from '@mui/icons-material';
import { userHasPermissions } from 'redux/actions/userLogin';

const System = (props) => {
  const styles = useStyles();
  const [open, setOpen] = useState(!!props.allOpen || get(props, 'alwaysOpen', false));

  const { pssvSystem, search } = props;
  const title = get(pssvSystem, 'name', '');
  const pssvChecklists = get(pssvSystem, 'children', []);
  const clhaschildren = pssvChecklists.find((c) => size(c.children));

  useEffect(() => {
    if (search) {
      setOpen(true);
    } else {
      setOpen(!!props.allOpen || get(props, 'alwaysOpen', false));
    }
  }, [pssvSystem]);

  return !size(pssvChecklists) || !clhaschildren ? null : (
    <>
      <ListItem className={styles.listItem} button onClick={() => setOpen(!open)}>
        <ListItemText primary={title} primaryTypographyProps={{ variant: 'button' }} />
        <ListItemIcon>
          <Icon>{`arrow_drop_${open ? 'up' : 'down'}`}</Icon>
        </ListItemIcon>
      </ListItem>
      <Collapse in={open} unmountOnExit>
        <List disablePadding className={styles.marginLeft}>
          {pssvChecklists.map((check, i) => (
            <Checklist {...props} key={get(check, 'checklistId')} pssvChecklist={check} />
          ))}
        </List>
      </Collapse>
    </>
  );
};

const Checklist = (props) => {
  const styles = useStyles();
  const [open, setOpen] = useState(get(props, 'allOpen', false));
  const { pssvSystem, pssvChecklist, search } = props;
  const title = get(pssvChecklist, 'name', '');

  const pssvCheckpoints = get(pssvChecklist, 'children', []).filter((c) => c.checkpointId);

  useEffect(() => {
    const isMatch = get(props, 'pssvTag.pssvChecklist.id') === get(pssvChecklist, 'checklistId') || get(props, 'pssvTag.pssvChecklist.checklistId') === get(pssvChecklist, 'checklistId');
    if (search || isMatch) {
      setOpen(true);
    } else {
      setOpen(get(props, 'allOpen', false));
    }
  }, [pssvChecklist]);

  const tagMe = (pssvCheckpoint) => {
    props.setPssvTag?.({
      pssvSystem,
      pssvChecklist,
      pssvCheckpoint,
      pssvSubScore: { notes: props.canWallterSubscore ? search : undefined, score: 0, images: [] },
      isEditMode: false,
    });
    props.setHighlightedElement?.(`${get(props, 'realm.id', '')}_${get(pssvCheckpoint, 'checkpointId')}_${get(pssvCheckpoint, 'updatedAt')}`);
    if (!props.thinking && props.onNavigateRight) props.onNavigateRight();
  };

  return !size(pssvCheckpoints) ? null : (
    <>
      <ListItem className={styles.listItem} button onClick={() => setOpen(!open)} id={pssvChecklist.checklistId}>
        <ListItemText primary={title} />
        <ListItemIcon className={styles.marginRight}>
          <Icon>{`arrow_drop_${open ? 'up' : 'down'}`}</Icon>
        </ListItemIcon>
      </ListItem>
      {!props.thinking && (
        <Collapse in={open}>
          <List disablePadding>
            {pssvCheckpoints.map((check) => {
              const currentKey = `${get(props, 'realm.id', '')}_${get(check, 'checkpointId')}_${get(check, 'updatedAt')}`;
              return <Checkpoint key={currentKey} pssvCheckpoint={check} onClick={tagMe} {...props} highlight={props.highlightedElement === currentKey} />;
            })}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const Checkpoint = (props) => {
  const { pssvCheckpoint, search, isMobile, realm } = props;
  const [thinking, setThinking] = useState(false);
  const [optimisticScored, setOptimisticScored] = useState(null);
  const styles = useStyles();
  // Derive these directly from props instead of mirroring into state via
  // useEffect — that caused the auto-pass status to "not stick" when a
  // System/Checklist Collapse re-mounted the component (initial state was
  // empty until the [subTrack] effect ran, and the optimistic toggle was lost).
  const relevantSubs = get(pssvCheckpoint, 'subscores', []).filter((c) => c.realmId === Number(get(props, 'realm.id')));
  const autos = relevantSubs.filter((f) => get(f, 'notes') === AUTOTAG);
  const nonautos = relevantSubs
    .filter((f) => get(f, 'notes') !== AUTOTAG)
    .map((a) => ({
      ...a,
      scorecardId: get(props, 'scorecard.id'),
      checklistId: get(props, 'pssvChecklist.checklistId'),
      checkpointId: pssvCheckpoint.checkpointId,
      supersetId: get(props, 'pssvSystem.supersetId'),
      checklist: props.pssvChecklist,
      _subscore: { ...pssvCheckpoint, ...a },
    }));

  const hasCriteria = get(pssvCheckpoint, 'criteria');
  const classes = cpUseStyles();
  // This is checking both to see if the scorecard uses room-checkpoints (represented by `allOpen`)
  // And to check if the interface even has the function to operate (`submitEmptySubscore`) -- which excludes the admin interface
  const showAutoScore = get(props, 'allOpen', false) && !!get(props, 'submitEmptySubscore', false);

  const selectCheckpoint = () => {
    props.onClick(pssvCheckpoint);
  };

  const visibleSubSize = size(nonautos);

  const isScored = !!size(relevantSubs);
  const hasAutos = !!size(autos);
  // Show the post-click state immediately so fast auto-passing doesn't flicker
  // through a spinner before landing on the final icon.
  const displayedScored = optimisticScored ?? isScored;
  const isDisabled = isScored && visibleSubSize;

  useEffect(() => {
    if (optimisticScored !== null && optimisticScored === isScored) {
      setOptimisticScored(null);
    }
  }, [isScored, optimisticScored]);

  const thinkQuick = async () => {
    setThinking(true);
    await props.updateScores();
    setThinking(false);
  };

  const createEmptySubscore = async () => {
    const willDelete = hasAutos || isScored;
    setOptimisticScored(!isScored);
    try {
      setThinking(true);
      if (willDelete) {
        await deleteSubsOnCheckpoint({ ...pssvCheckpoint, realmId: get(props, 'realm.id') });
      } else {
        await props.submitEmptySubscore(pssvCheckpoint);
      }
    } catch (err) {
      setOptimisticScored(null);
    } finally {
      props.updateScores?.();
      setThinking(false);
    }
  };

  return (
    <div className={classes.topic}>
      <div className={classes.topicFlex}>
        <ListItem button onClick={selectCheckpoint} style={{ paddingLeft: 0 }} className={props.highlight ? styles.highlightContainer : ''}>
          <div className={classes.rowTop}>
            <div style={{ marginRight: 8 }}>
              <ScoreIcon selected={false} center isAddMenu score={!displayedScored ? null : 0} />
            </div>
            <Typography variant="body2">
              <strong>{getHighlightedText(pssvCheckpoint.name || '', search)}</strong>
              &nbsp;
              {getHighlightedText(hasCriteria || '', search)}
            </Typography>
          </div>
        </ListItem>
        {showAutoScore && <IcoButton onClick={createEmptySubscore} disabled={isDisabled || thinking} variant="none" icon={displayedScored ? 'close' : 'check'} loadingProps={{ size: 16 }} />}
      </div>
      {!!visibleSubSize && (
        <div style={{ marginLeft: 36 }}>
          <SubScoreCollapse
            {...props}
            onAdd={thinkQuick}
            canEdit
            pssvCheckpoint={{
              ...pssvCheckpoint,
              isDialog: { isMobile },
              realm: realm,
              subscores: nonautos,
            }}
          />
        </div>
      )}
    </div>
  );
};

export const RealmsCheckpointsList = (props) => {
  const { filteredRealms, allScoreData, scorecard, tierChildLabel, realmDrop, pssvTag, setPssvTag, realm, setRealm, isPreview } = props;
  const [search, setSearch] = useState('');
  const [suggestedCpIds, setSuggestedCpIds] = useState([]);
  const debouncedSearch = useDebounce(search, 1000);
  const [thinking, setThinking] = useState(false);
  const [treed, setTreed] = useState();
  const inputRef = useRef();
  const { isMobileXS } = useScreen();
  const styles = useStyles();
  const showSearch = get(props, 'showSearch', true);
  const [wallterShimmer, setWallterShimmer] = useState(false);
  const canWallterSubscore = userHasPermissions(['wallterSubscore']);
  const lastWallterQueryRef = useRef('');

  const [state, setState] = useState({
    useCps: true,
  });

  const hasRealmCheckpoints = !!size(get(props, 'scorecard.config.roomCheckpoints', []));

  const realmCheckpoints = (() => {
    if (realm && get(scorecard, 'config.roomCheckpoints')) {
      const obj = get(scorecard, 'config.roomCheckpoints', []).find((r) => r.id === realm.id);
      return !size(get(obj, 'cps')) ? undefined : obj;
    }
  })();

  const showBlank = !size(treed);

  const runFilter = async (d = allScoreData) => {
    try {
      setThinking(true);

      let roomcps = realmCheckpoints && state.useCps ? realmCheckpoints : undefined;

      // If there are suggested checkpoints from Wallter, merge them with the existing roomcps
      if (suggestedCpIds && suggestedCpIds.length && roomcps) {
        // Clone roomcps to avoid mutating original
        roomcps = { ...roomcps, cps: [...(roomcps.cps || []), ...suggestedCpIds] };
      }

      const { hasChildren } = await filterData(
        d,
        {
          search: debouncedSearch,
          value: 'any',
          roomcps,
          suggestedCpIds,
        },
        false
      );
      setTreed(hasChildren);
    } catch (err) {
      console.warn(err);
    } finally {
      setThinking(false);
    }
  };

  useEffect(() => {
    if (props.open) {
      runFilter(allScoreData);
    }
  }, [debouncedSearch, allScoreData, props.open, realm, state.useCps]);

  useEffect(() => {
    if (props.open && debouncedSearch !== '' && lastWallterQueryRef.current !== debouncedSearch) {
      lastWallterQueryRef.current = debouncedSearch;
      canWallterSubscore && handleAskWallter();
    }
  }, [props.open, debouncedSearch, canWallterSubscore]);

  const reset = () => {
    setPssvTag({});
    setSearch('');
    setThinking(false);
  };

  const showToggle = !!realmCheckpoints && !!search;

  const searchChange = (val) => {
    setSearch(val);
    setThinking(true);
    if (val === '') {
      setWallterShimmer(false);
      setState((s) => ({ ...s, useCps: true }));
    }
  };

  const submitEmptySubscore = async (pssvCheckpoint) => {
    try {
      setThinking(true);
      let realm = get(props, 'realm');

      const isNewrealm = typeof realm === 'string' || realm instanceof String;
      if (isNewrealm) {
        const result = await sendData(
          {
            url: `/realms`,
            method: 'post',
            data: {
              data: {
                type: 'realms',
                attributes: {
                  name: realm,
                  tier: get(props, 'realms.realmFilter.tier', 'room'),
                  parent_id: get(first(get(props, 'filteredRealms.data', [])), 'parentId') || get(pssvCheckpoint, 'assessmentRealmId', undefined),
                },
              },
            },
          },
          { normalize: { build: ['realms'] } }
        );

        get(props, 'realms.update', () => 0)();
        realm = first(result);
      }

      const subscore = get(pssvCheckpoint, '_subscore');

      const data = {
        data: {
          type: 'sub-scores',
          id: get(subscore, 'id'),
          attributes: stringObj({
            superset_id: get(pssvCheckpoint, 'supersetId', ''),
            assessment_id: get(pssvCheckpoint, 'assessmentId', ''),
            scorecard_id: get(pssvCheckpoint, 'scorecardId', ''),
            checklist_id: get(pssvCheckpoint, 'checklistId', ''),
            checkpoint_id: get(pssvCheckpoint, 'checkpointId', ''),
            checkpoint_option_id: undefined,
            scorecard_option_id: undefined,
            score: '100',
            criteria: get(pssvCheckpoint, 'criteria', ''),
            realm_id: get(realm, 'id'),
            notes: AUTOTAG,
            images: [],
          }),
        },
      };

      await createSubScore(data, { removeAutos: false });
    } catch (err) {
      alert(err);
    } finally {
      setThinking(false);
    }
  };
  const selectRealm = (e, realm) => {
    if (!realm) {
      return;
    }
    setRealm(realm);
  };

  const getRealmDir = () => {
    const ind = (filteredRealms.data || []).findIndex((r) => r.id === get(realm, 'id'));

    return {
      prev: filteredRealms.data[ind - 1],
      next: filteredRealms.data[ind + 1],
      cur: filteredRealms.data[ind],
    };
  };
  const classes = useStyles();

  const handleAskWallter = async () => {
    if (debouncedSearch === '') return;
    const wallterResponse = await sendData({
      url: `/wallter?scorecard_id=${scorecard?.id}&query=${debouncedSearch}`,
    });

    const responseData = wallterResponse.data || {};

    // filter out the request id key
    const items = Object.values(responseData).filter((item) => typeof item === 'object' && item.checkpoint_id);

    setWallterShimmer(items.length > 0 && items[0].distance < 1.3);

    const checkpointIds = items.map((item) => item.checkpoint_id || item.checkpointId).filter(Boolean);

    setSuggestedCpIds(checkpointIds);
  };

  useEffect(() => {
    runFilter(allScoreData);
  }, [suggestedCpIds]);

  const handleWallterCreateSubscore = () => {
    const mostRelevantCpId = first(suggestedCpIds);
    if (!treed) return;
    let foundSystem, foundChecklist, foundCheckpoint;

    for (const system of get(treed, '0.children', [])) {
      for (const checklist of get(system, 'children', [])) {
        const checkpoint = get(checklist, 'children', []).find((cp) => cp.checkpointId.toString() === mostRelevantCpId);
        if (checkpoint) {
          foundSystem = system;
          foundChecklist = checklist;
          foundCheckpoint = checkpoint;
          break;
        }
      }
      if (foundSystem) break;
    }

    const pssvSystem = foundSystem;
    const pssvChecklist = foundChecklist;
    const pssvCheckpoint = foundCheckpoint;

    props.setPssvTag?.({
      pssvSystem: pssvSystem,
      pssvChecklist: pssvChecklist,
      pssvCheckpoint: pssvCheckpoint,
      pssvSubScore: { notes: debouncedSearch, score: 0, images: [] },
      isEditMode: false,
    });
    props.setHighlightedElement?.(`${get(props, 'realm.id', '')}_${get(pssvCheckpoint, 'checkpointId')}_${get(pssvCheckpoint, 'updatedAt')}`);
    !props.thinking && props.onNavigateRight && props.onNavigateRight();
  };

  const renderFooter = () => {
    if (hasRealmCheckpoints) {
      const realmDir = getRealmDir();
      return (
        <div>
          <Row center style={{ padding: 10, alignItems: 'flex-start', margin: 'auto 4px' }}>
            <div className={classes.dirButtonContainer}>
              <IcoButton size={'small'} color="secondary" disabled={!realmDir.prev} icon="chevron_left" onClick={() => selectRealm(null, realmDir.prev)} title="PREV" />
              <div>
                <Text type="label_medium">{get(realmDir, 'prev.name', '')}</Text>
              </div>
            </div>
            <div style={{ minWidth: 250 }}>
              {realmDrop({
                variant: 'standard',
                placeholder: `CHANGE ${tierChildLabel}`,
                classes: { root: classes.root, input: classes.input },
              })}
            </div>
            <div className={classes.dirButtonContainer}>
              <IcoButton size={'small'} color="secondary" disabled={!realmDir.next} icon="chevron_right" onClick={() => selectRealm(null, realmDir.next)} title="NEXT" />
              <div>
                <Text type="label_medium">{get(realmDir, 'next.name', '')}</Text>
              </div>
            </div>
          </Row>
          <Row center className={classes.doneRoomsRow}>
            <Column center>
              <Text type="label_large">Done with all rooms?</Text>
              <Button title="REVIEW SCORES" onClick={onReview} style={{ marginBottom: 16 }} />
            </Column>
          </Row>
        </div>
      );
    } else {
      return (
        <Row center>
          <div style={{ padding: 10 }}>
            {realmDrop?.({
              variant: 'outlined',
              size: 'small',
              placeholder: `CHANGE ${tierChildLabel}`,
              classes: { root: classes.root, input: classes.input },
            })}
          </div>
        </Row>
      );
    }
  };

  const onReview = () => {
    props.onReview && props.onReview();
  };

  useEffect(() => {
    if (inputRef.current && !isMobileXS) {
      inputRef.current.focus();
    }
  }, [isMobileXS, props.open]);

  return (
    <Box className={classes.listContainer}>
      <Typography id="tagSubtitle" variant="subtitle2" className={classes.subtitle}>
        {get(props, 'subTitle', 'Link To Criteria')}
      </Typography>

      {props.open && showSearch && (
        <Box className={classes.headerContainer}>
          <TextField
            fullWidth
            variant="outlined"
            id="search"
            value={search}
            label={canWallterSubscore ? 'Describe your observation' : 'Search'}
            onChange={(e) => searchChange(e.target.value)}
            inputRef={inputRef}
            InputProps={{
              endAdornment: (
                <InputAdornment disablePointerEvents={!search} position="end">
                  <IconButton onClick={() => searchChange('')} size="large">
                    {thinking ? <Loader size={20} /> : <Icon>{search ? 'close' : 'search'}</Icon>}
                  </IconButton>
                  {canWallterSubscore && (
                    <IcoButton onClick={handleWallterCreateSubscore} variant="filled" color="secondary" size="small" disabled={!search} className={wallterShimmer ? classes.shimmer : undefined}>
                      <PhotoFilter />
                    </IcoButton>
                  )}
                </InputAdornment>
              ),
            }}
          />

          {showToggle && (
            <Row>
              <Text>{`Searching "${get(realm, 'name')}". Show all criteria?`}</Text>
              <Switch checked={!state.useCps} onChange={() => setState((s) => ({ ...s, useCps: !s.useCps }))} />
            </Row>
          )}
        </Box>
      )}

      <Box className={classes.scrollableListContainer}>
        {!!showBlank && (
          <div className={styles.centerColumn} style={{ padding: 20 }}>
            <span>{`There are no results that meet your search criteria.`}</span>
          </div>
        )}

        {!showBlank && !!props.open && (
          <List component="nav" className={classes.systemContainer}>
            {get(treed, '0.children', []).map((sys, i) => (
              <System
                {...props}
                key={sys.supersetId}
                pssvSystem={sys}
                search={debouncedSearch}
                setPssvTag={setPssvTag}
                pssvTag={pssvTag}
                realm={realm}
                submitEmptySubscore={!!get(props, 'updateScores') ? submitEmptySubscore : undefined}
                scorecard={props.scorecard}
                scorecardConfig={get(scorecard, 'config', {})}
                allOpen={realmCheckpoints && get(props, 'realms.data.0.tier') === 'room'}
                alwaysOpen={size(get(treed, '0.children', [])) === 1 || sys.supersetId === get(props, 'pssvTag.pssvSystem.id')}
                isMobile={isMobileXS}
                realms={props.realms}
                assessment={props.assessment}
                updateScores={props.updateScores}
                canWallterSubscore={canWallterSubscore}
              />
            ))}
          </List>
        )}
        {!isPreview && renderFooter()}
      </Box>
    </Box>
  );
};

export default RealmsCheckpointsList;
